import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import { ErrorState } from '../components/ui';

// Get user's current language for menu item names
const getLocalizedField = (item, field, lang) => {
  const langCode = lang === 'id' ? 'id' : lang === 'ar' ? 'ar' : lang === 'ms' ? 'ms' : 'en';
  return item[`${field}_${langCode}`] || item[`${field}_en`] || '';
};

// Format price based on currency
const formatPrice = (price, currency = 'IDR') => {
  if (!price) return null;

  const formatters = {
    IDR: new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }),
    SGD: new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }),
    MYR: new Intl.NumberFormat('ms-MY', { style: 'currency', currency: 'MYR' }),
    SAR: new Intl.NumberFormat('ar-SA', { style: 'currency', currency: 'SAR' }),
    AUD: new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }),
    USD: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
  };

  return formatters[currency]?.format(price) || `${currency} ${price}`;
};

// Menu Item Card Component
function MenuItemCard({ item, lang }) {
  const { t } = useTranslation();
  const name = getLocalizedField(item, 'name', lang);
  const description = getLocalizedField(item, 'description', lang);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 group">
      {/* Image */}
      <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {item.is_popular && (
            <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-medium rounded-full">
              {t('menu.popular')}
            </span>
          )}
          {item.is_new && (
            <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
              {t('menu.new')}
            </span>
          )}
        </div>

        {/* Dietary badges */}
        <div className="absolute top-2 right-2 flex gap-1">
          {item.is_spicy && (
            <span className="w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center" title="Spicy">
              🌶️
            </span>
          )}
          {item.is_vegetarian && (
            <span className="w-6 h-6 bg-green-600 text-white text-xs rounded-full flex items-center justify-center" title="Vegetarian">
              🌱
            </span>
          )}
          {item.is_halal && (
            <span className="w-6 h-6 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center" title="Halal">
              ✓
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-lg mb-1">{name}</h3>
        {description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{description}</p>
        )}

        {/* Price */}
        <div className="flex items-center justify-between">
          {item.price ? (
            <span className="text-primary-600 font-bold text-lg">
              {item.price_display || formatPrice(item.price, item.currency)}
            </span>
          ) : (
            <span className="text-gray-400 text-sm italic">
              {t('menu.priceNotSet')}
            </span>
          )}

          {/* Spice level indicator */}
          {item.spice_level > 0 && (
            <div className="flex items-center gap-0.5" title={`Spice level: ${item.spice_level}/3`}>
              {[...Array(3)].map((_, i) => (
                <span
                  key={i}
                  className={`text-sm ${i < item.spice_level ? 'text-red-500' : 'text-gray-300'}`}
                >
                  🌶️
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MenuBranch() {
  const { branchId } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [branch, setBranch] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchData();
  }, [branchId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch branch info by slug or id
      let { data: branchData, error: branchError } = await supabase
        .from('website_branches')
        .select('*')
        .eq('slug', branchId)
        .eq('is_active', true)
        .single();

      // If not found by slug, try by id
      if (branchError || !branchData) {
        const result = await supabase
          .from('website_branches')
          .select('*')
          .eq('id', branchId)
          .eq('is_active', true)
          .single();

        branchData = result.data;
      }

      if (branchData) {
        setBranch(branchData);

        // Fetch categories
        const { data: catData } = await supabase
          .from('website_menu_categories')
          .select('*')
          .eq('is_active', true)
          .order('sort_order');

        setCategories(catData || []);

        // Fetch menu items
        const { data: itemsData, error: itemsError } = await supabase
          .from('website_menu_items')
          .select(`
            *,
            category:website_menu_categories(id, name_en, name_id, name_ar, name_ms)
          `)
          .eq('is_active', true)
          .order('sort_order');

        if (itemsError) throw itemsError;

        // Fetch branch-specific prices
        const { data: pricesData, error: pricesError } = await supabase
          .from('website_menu_item_prices')
          .select('*')
          .eq('branch_id', branchData.id)
          .eq('is_available', true);

        if (!pricesError && pricesData && pricesData.length > 0) {
          // Merge prices with menu items - only show items that have prices for this branch
          const priceMap = new Map(pricesData.map(p => [p.menu_item_id, p]));
          const itemsWithPrices = itemsData
            .filter(item => priceMap.has(item.id)) // Only show items with prices for this branch
            .map(item => {
              const branchPrice = priceMap.get(item.id);
              return {
                ...item,
                price: branchPrice.price,
                currency: branchPrice.currency,
                price_display: branchPrice.price_display || formatPrice(branchPrice.price, branchPrice.currency),
              };
            });
          setMenuItems(itemsWithPrices);
        } else {
          // If no branch-specific prices, show all menu items with base prices
          setMenuItems(itemsData || []);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter menu items by category
  const filteredItems = useMemo(() => {
    if (selectedCategory === 'all') return menuItems;
    return menuItems.filter(item => item.category_id === selectedCategory);
  }, [menuItems, selectedCategory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <span className="text-gray-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <ErrorState onRetry={fetchData} />
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <p className="text-gray-500 mb-4">Branch not found</p>
        <Link to="/branches" className="btn-primary">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Link */}
          <Link
            to={`/branches/${branch.slug || branch.id}`}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </Link>

          {/* Page Header */}
          <div className="flex items-center gap-4">
            {branch.featured_image_url && (
              <img
                src={branch.featured_image_url}
                alt={branch.name}
                className="w-16 h-16 rounded-xl object-cover"
              />
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-bold text-gray-900">
                {t('menu.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {branch.name} - {branch.city}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {t('menu.allCategories')}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {getLocalizedField(category, 'name', lang)}
              </button>
            ))}
          </div>
        )}

        {/* Menu Items Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <MenuItemCard key={item.id} item={item} lang={lang} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <p className="text-gray-500">{t('menu.noItems')}</p>
            <p className="text-sm text-gray-400 mt-2">
              No menu items have been configured for this branch yet.
            </p>
            <Link to="/menu" className="inline-block mt-4 text-primary-600 hover:text-primary-700 font-medium">
              View Full Menu
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
